import { z } from 'zod';
import { slugSchema } from './common.js';
import { collectionKindSchema, illustrationObjectSchema, subjectFamilySchema, subjectSchema, tintFields, verbSchema, } from './content.js';
/**
 * What Home renders, in one read.
 *
 * Screen-shaped rather than four resource reads the client stitches together: 005 §4's
 * rule is *"always make it obvious what the user should do next"*, and a client that
 * derived that from `/collections` plus its own attempt history would be a second
 * implementation of it, free to disagree about which lesson is live.
 *
 * Everything here is derived. Nothing in it is stored in this shape.
 */
/**
 * Where a lesson sits relative to the learner.
 *
 * Three values, not the five `PathNodeState` carries in packages/ui. `checkpoint` is
 * absent because a checkpoint is a *kind* of lesson that also has a position — it is
 * the `checkpoint` flag below, and it still reports `done`, `now` or `locked`. `ahead`
 * is absent because the locked design gates strictly: everything past the current
 * lesson is drawn locked, and emitting a value no query can produce would be
 * vocabulary a client had to handle and never see.
 */
export const pathNodeStateSchema = z.enum(['done', 'now', 'locked']);
export const lessonScoreSchema = z
    .object({
    correct: z.number().int().min(0),
    total: z.number().int().min(0),
})
    .describe('Graded scenes, and how many of them were fully right.');
export const homeLessonSchema = z
    .object({
    slug: slugSchema,
    title: z.string().min(1).max(120),
    summary: z.string().min(1).max(300),
    verb: verbSchema.optional(),
    estimatedMinutes: z.number().int().min(1).max(60),
    objectName: illustrationObjectSchema.optional(),
    /**
     * Derived from the subject of the course that owns this lesson's module, never
     * stored — `subjectFamily` in `content.ts` is the mapping. A lesson carries no
     * subject of its own: spec 012 §20 puts subject on the question, because one
     * lesson mixes them.
     */
    ...tintFields,
    state: pathNodeStateSchema,
    score: lessonScoreSchema
        .optional()
        .describe('The best completed attempt. Done nodes only.'),
    /**
     * Whether this lesson is a checkpoint — `005` §4 asks Home to show them.
     *
     * Derived from the verb rather than stored: `simulate` is the top of §3's
     * progression, and a lesson that asks the learner to run the whole thing is what a
     * checkpoint is. Storing a flag beside the verb would be a second way to say it,
     * free to disagree.
     */
    checkpoint: z.boolean(),
})
    .describe('One lesson on the path, with where the learner stands on it.');
export const homeJourneySchema = z
    .object({
    slug: slugSchema,
    kind: collectionKindSchema,
    title: z.string().min(1).max(120),
    completed: z.number().int().min(0),
    total: z.number().int().min(1),
    lessons: z.array(homeLessonSchema).min(1),
})
    .describe('The collection the spine draws, in CollectionLesson order.');
export const homeStatsSchema = z
    .object({
    xpTotal: z.number().int().min(0),
    streakCurrent: z.number().int().min(0),
    streakLongest: z.number().int().min(0),
    lessonsCompleted: z.number().int().min(0),
})
    .describe('The status row. A projection of the ledger, read straight off UserStats.');
export const homeRecommendationSchema = z
    .object({
    slug: slugSchema,
    kind: collectionKindSchema,
    title: z.string().min(1).max(120),
    /**
     * The collection's own one-liner, never generated prose. The screen needs a reason
     * to print, and an invented one would claim a personalisation that does not exist
     * until onboarding ships — CONT-01.
     */
    summary: z.string().min(1).max(300),
    /**
     * The tint, the size and the way in. CONT-01 sent the four fields Home's empty
     * state prints; the path-complete milestone draws the same recommendation as a
     * collection row, which needs what every other collection row needs — CONT-04.
     */
    ...tintFields,
    lessonCount: z.number().int().min(1),
    estimatedMinutes: z.number().int().min(0),
    /**
     * The first lesson of it. Taking a recommendation has to start something: a
     * learner is in a collection from their first attempt at one of its lessons
     * (`progress.projector.ts`), so a slug pointing at the collection would record
     * nothing and Home would go on offering the path they just finished.
     */
    firstLessonSlug: slugSchema,
})
    .describe('A published collection the learner has not touched.');
export const homeStreakReturnSchema = z
    .object({
    kind: z.enum(['kept', 'broken']),
    /**
     * Kept, the run the learner is still on. Broken, the run that ended — the
     * number that was lost, which `stats.streakCurrent` no longer carries because
     * the same response has already zeroed it.
     */
    days: z.number().int().min(1),
})
    .describe('What the streak is really doing on a return after a gap.');
/**
 * The one lesson every learning tab offers at its top — UI-44.
 *
 * Why it is `start`, `resume`, `next` or `review` is the server's to decide, because
 * which lesson follows from it: the journey's current lesson, the first lesson of a
 * course the learner has not touched once the journey is done, and the lowest-scored
 * lesson they have finished once every course is started. Home and Explore both carry
 * it so the two tabs cannot offer different lessons.
 */
export const nextUpSchema = z
    .object({
    reason: z.enum(['start', 'resume', 'next', 'review']),
    /** The collection the lesson is offered from. */
    collection: z.string().min(1).max(120),
    /** Lessons done in that collection. Absent on a review, which is not a way through one. */
    progress: z
        .object({
        completed: z.number().int().min(0),
        total: z.number().int().min(1),
    })
        .optional(),
    lesson: z.object({
        slug: slugSchema,
        title: z.string().min(1).max(120),
        ...tintFields,
    }),
})
    .describe('The lesson the Next Up hero opens, and why it is that one.');
export const homeNewTopicSchema = z
    .object({
    topic: subjectSchema,
    family: subjectFamilySchema,
    /** The lesson its Start opens, and the one whose first attempt removes it. */
    firstLesson: slugSchema,
})
    .describe('A topic the learner chose that became ready after they chose it, in the last 14 days, not yet started.');
const dailyProgressSchema = z
    .object({
    done: z.number().int().min(0),
    target: z.number().int().min(1),
})
    .describe('Lessons counted toward the daily target today, and the target.');
export const homeResponseSchema = z
    .object({
    stats: homeStatsSchema,
    /**
     * Absent only when there is no published collection to draw. A learner with no
     * attempts is started on an arbitrary but stable path rather than an empty screen
     * — the owner's decision of 2026-09-21, recorded in `docs/decisions/deferred.md`.
     */
    journey: homeJourneySchema.optional(),
    /**
     * The Continue card. Always the `now` lesson of `journey` when there is one, sent
     * separately because the hero is the answer to "what do I do next" and the client
     * should not have to search an array to find it.
     */
    next: homeLessonSchema.optional(),
    /**
     * Where an unfinished run of `next` reopens, as the player counts scenes: the
     * same point `GET /lessons/:slug/resume` opens on (UI-66). Absent when `next`
     * has no run to resume.
     */
    partway: z
        .object({
        scene: z.number().int().min(1),
        total: z.number().int().min(1),
    })
        .optional(),
    /**
     * Whether the learner's daily target — 1, 2 or 3 lessons — is met on their local date.
     * True does not close the day: the path stays open and extra lessons move it.
     */
    goalMet: z.boolean(),
    /**
     * The count `goalMet` is decided from (ONB-34). `done` is not capped at `target`:
     * extra lessons are real, and the streak sheet prints them.
     */
    today: dailyProgressSchema,
    /**
     * The streak moment a return has earned, absent when the learner has already
     * been active today or has no run to speak of — CONT-06. The client decides
     * only whether it has shown it yet today; which state it is, and the number
     * on it, are the server's, because the client cannot see the ledger's dates.
     */
    streakReturn: homeStreakReturnSchema.optional(),
    /** Absent only when nothing is published. */
    nextUp: nextUpSchema.optional(),
    /**
     * The next lesson of the learner's onboarding path, while `journey` is a collection
     * that does not hold it: lessons played elsewhere took Home over, and this is the
     * way back. Starting it puts the path back on Home — `one-lesson-detour.md`.
     */
    pathReturn: nextUpSchema.optional(),
    /** The updates sheet's topics, newest first (ONB-20). */
    newTopics: z.array(homeNewTopicSchema),
})
    .describe('Everything the Home screen shows (005 §4).');
//# sourceMappingURL=home.js.map